/**
 * BookCallPage — subscription-gated info-call booking surface,
 * mounted at /#/book-call. Three states:
 *
 *   1. Not signed in            : warm paywall pitch with sign-up
 *                                  CTA + "$34.99 credited if you buy
 *                                  the cert" copy.
 *   2. Signed in, no active sub : same paywall, button routes to
 *                                  the in-app paywall instead of
 *                                  signup.
 *   3. Signed in + active sub   : header + embedded GHL Cal widget
 *                                  so they book without leaving the
 *                                  platform shell.
 *
 * Voice: no em dashes, no defensive copy. Calls aren't a punishment-
 * gated thing — they're a member benefit, framed accordingly.
 */
import React, { useEffect, useState } from "react";
import {
  Calendar, ShieldCheck, Loader2, ArrowRight, GraduationCap,
  CheckCircle2, MessageCircle, Users, Globe,
} from "lucide-react";
import { GOLD } from "../constants/theme";
import { BAM_LOGO_PNG } from "../constants/branding";
import { supabase } from "../../supabaseClient";
import { logEvent } from "../utils/analytics";

const WIDGET_URL = "https://go.byanymeanscoaches.com/widget/bookings/bam-coaches-certification";

export function BookCallPage() {
  const [loading, setLoading] = useState(true);
  const [signedIn, setSignedIn] = useState(false);
  const [hasActiveSub, setHasActiveSub] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!user) {
        setSignedIn(false);
        setLoading(false);
        logEvent("book_call_view", { state: "anon" });
        return;
      }
      setSignedIn(true);
      const { data: rows } = await supabase
        .from("profiles")
        .select("stripe_subscription_active")
        .eq("id", user.id)
        .limit(1);
      const active = !!rows?.[0]?.stripe_subscription_active;
      if (cancelled) return;
      setHasActiveSub(active);
      setLoading(false);
      logEvent("book_call_view", { state: active ? "subscribed" : "no_sub" });
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <PageShell><BrandedSpinner/></PageShell>;
  }

  // ── Subscribed: embed booking widget ────────────────────────────
  if (signedIn && hasActiveSub) {
    return (
      <PageShell wide>
        <div style={{
          maxWidth: 880, width: "100%",
          padding: "8px 22px 14px",
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 11, marginBottom: 8,
          }}>
            <div style={iconCircleSmall}><Calendar size={16} color={GOLD}/></div>
            <Eyebrow>Book your call</Eyebrow>
          </div>
          <h1 style={{
            fontSize: 28, fontWeight: 800, color: "#F5F0E8",
            margin: "0 0 8px", lineHeight: 1.2, letterSpacing: -0.3,
          }}>Pick a time that works</h1>
          <p style={{
            fontSize: 14, color: "#A09A8B", lineHeight: 1.65,
            margin: "0 0 24px", maxWidth: 560,
          }}>
            Quick conversation. We'll talk through whether the BAM Cert
            is the right fit for where you are. About 20 minutes. No
            pressure, no script.
          </p>
        </div>
        <div style={{
          maxWidth: 880, width: "100%",
          padding: "0 22px 60px",
          position: "relative", zIndex: 1,
        }}>
          <iframe
            title="Book a call"
            src={WIDGET_URL}
            style={{
              width: "100%", minHeight: 720, border: "none",
              borderRadius: 16, background: "#fff",
              boxShadow: `0 24px 60px rgba(0,0,0,0.6), 0 0 0 1px ${GOLD}33`,
            }}
            allow="payment"
          />
        </div>
      </PageShell>
    );
  }

  // ── Not signed in / no active sub: paywall pitch ────────────────
  const ctaLabel = signedIn ? "Subscribe to the platform" : "Get started, $34.99/mo";
  const onCtaClick = (e) => {
    e.preventDefault();
    logEvent("book_call_paywall_click", { state: signedIn ? "no_sub" : "anon" });
    window.location.hash = signedIn ? "#/platform" : "#/signup";
  };

  return (
    <PageShell>
      <Card>
        <div style={iconCircleStyle}><Calendar size={26} color={GOLD}/></div>
        <Eyebrow>Members only</Eyebrow>
        <h1 style={{
          fontSize: 26, fontWeight: 800, color: "#F5F0E8",
          margin: "0 0 12px", lineHeight: 1.25, letterSpacing: -0.3,
        }}>
          Calls are reserved for active members
        </h1>
        <p style={{
          fontSize: 14, color: "#A09A8B", lineHeight: 1.65,
          margin: "0 0 14px",
        }}>
          If you're serious enough to subscribe for a month, we're
          serious enough to talk. The $34.99 you spend this month gets
          refunded the moment you buy the cert, so a real conversation
          costs you nothing extra.
        </p>
        <SocialProof/>
        <div style={callCoversStyle}>
          <div style={{
            fontSize: 10, fontWeight: 800, color: GOLD,
            letterSpacing: 1.4, textTransform: "uppercase", marginBottom: 8,
          }}>What the call covers</div>
          <CallBullet>Whether the BAM Cert fits where you are in your coaching career</CallBullet>
          <CallBullet>What the six-month work window actually looks like, day to day</CallBullet>
          <CallBullet>Anything specific you're trying to build for your players or program</CallBullet>
        </div>
        <PrimaryButton onClick={onCtaClick}>
          {ctaLabel} <ArrowRight size={15} strokeWidth={2.5}/>
        </PrimaryButton>
        {!signedIn && (
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <a href="#/signin" style={{
              fontSize: 12, color: "#6C6658", textDecoration: "none",
              fontFamily: "'DM Sans', sans-serif",
            }}>
              Already have an account? Sign in
            </a>
          </div>
        )}
      </Card>
      <TrustFooter/>
    </PageShell>
  );
}

// ── Shared chrome (mirrors BuyCertPage so both pages feel cohesive) ──

function PageShell({ children, wide }) {
  return (
    <div style={shellStyle}>
      <style>{`
        @keyframes bamGoldSweep {
          0% { transform: translateX(-100%); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateX(100%); opacity: 0; }
        }
        @keyframes bamSpin { to { transform: rotate(360deg); } }
      `}</style>
      <div aria-hidden="true" style={{
        position: "fixed", inset: 0, zIndex: 0,
        background: `
          radial-gradient(70% 50% at 50% 0%, rgba(226,221,159,0.06) 0%, transparent 60%),
          radial-gradient(60% 60% at 50% 100%, rgba(226,221,159,0.03) 0%, transparent 70%),
          #050505
        `,
        pointerEvents: "none",
      }}/>
      <BrandHeader/>
      <div style={{
        position: "relative", zIndex: 1,
        flex: 1, width: "100%",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: wide ? "flex-start" : "center",
        padding: wide ? "8px 0 0" : "20px 16px 40px",
        gap: 18,
      }}>
        {children}
      </div>
    </div>
  );
}

function BrandHeader() {
  return (
    <div style={{
      position: "relative", zIndex: 1,
      padding: "22px 24px 14px",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <a href="https://byanymeanscoaches.com" target="_blank" rel="noopener noreferrer"
        style={{
          display: "inline-flex", alignItems: "center", gap: 11,
          textDecoration: "none", opacity: 0.92,
        }}>
        <img src={BAM_LOGO_PNG} alt="" style={{
          width: 28, height: 28,
          filter: `drop-shadow(0 0 8px ${GOLD}55)`,
        }}/>
        <div style={{ lineHeight: 1.05 }}>
          <div style={{
            fontFamily: "'Bebas Neue', Impact, sans-serif",
            fontSize: 16, letterSpacing: 3, color: GOLD,
          }}>BY ANY MEANS</div>
          <div style={{
            fontSize: 9, letterSpacing: 1.8,
            color: "#6C6658", textTransform: "uppercase",
            marginTop: 1, fontWeight: 700,
          }}>Coaches Platform</div>
        </div>
      </a>
    </div>
  );
}

function Card({ children }) {
  return (
    <div style={{
      position: "relative",
      maxWidth: 540, width: "100%",
      background: "linear-gradient(180deg, #131313, #0A0A0A)",
      border: `1px solid ${GOLD}55`,
      borderRadius: 20,
      boxShadow: `0 32px 80px rgba(0,0,0,0.65), inset 0 1px 0 ${GOLD}22, 0 0 0 1px rgba(0,0,0,0.4)`,
      padding: "30px 28px 26px",
      overflow: "hidden",
    }}>
      <div aria-hidden="true" style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg, transparent 0%, ${GOLD} 50%, transparent 100%)`,
        animation: "bamGoldSweep 6s ease-in-out infinite",
      }}/>
      {children}
    </div>
  );
}

function TrustFooter() {
  return (
    <div style={{
      position: "relative", zIndex: 1,
      maxWidth: 540, width: "100%",
      display: "flex", alignItems: "center", justifyContent: "center",
      gap: 14, flexWrap: "wrap",
      padding: "6px 16px 2px",
      fontSize: 11, color: "#5A5550",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <span style={{display: "inline-flex", alignItems: "center", gap: 5}}>
        <ShieldCheck size={12} color="#5A5550"/>
        Secure platform via Stripe
      </span>
      <span style={{opacity: 0.5}}>·</span>
      {/* A thumb-sized target without moving anything (2026-11-04 sweep: it was 14px tall). */}
      <a href="mailto:team@byanymeanscoaches.com" style={{
        color: "#5A5550", textDecoration: "underline", display: "inline-block", padding: "12px 4px", margin: "-12px -4px",
      }}>Questions? Email us</a>
    </div>
  );
}

function SocialProof() {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 10,
      padding: "0 0 18px",
    }}>
      <Stat Icon={Users}  value="100+" label="Certified coaches"/>
      <span style={{ width: 1, height: 22, background: `${GOLD}33` }}/>
      <Stat Icon={Globe}  value="20+"  label="Countries"/>
    </div>
  );
}

function Stat({ Icon, value, label }) {
  return (
    <div style={{display: "inline-flex", alignItems: "center", gap: 7}}>
      <Icon size={13} color={GOLD}/>
      <span style={{
        fontSize: 13, fontWeight: 800, color: "#F5F0E8",
        fontFamily: "'JetBrains Mono', monospace",
      }}>{value}</span>
      <span style={{
        fontSize: 11, color: "#A09A8B",
        fontWeight: 500,
      }}>{label}</span>
    </div>
  );
}

function CallBullet({ children }) {
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 9,
      padding: "5px 0",
    }}>
      <CheckCircle2 size={14} color={GOLD} strokeWidth={2.5} style={{
        flexShrink: 0, marginTop: 1,
      }}/>
      <div style={{
        fontSize: 13, color: "#C9C3B3", lineHeight: 1.5,
      }}>{children}</div>
    </div>
  );
}

function BrandedSpinner() {
  return (
    <div style={{
      width: 38, height: 38,
      borderRadius: "50%",
      border: `2.5px solid ${GOLD}22`,
      borderTopColor: GOLD,
      animation: "bamSpin 0.85s linear infinite",
    }}/>
  );
}

function Eyebrow({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 800, color: GOLD,
      letterSpacing: 1.6, textTransform: "uppercase",
    }}>{children}</div>
  );
}

const iconCircleStyle = {
  width: 56, height: 56, borderRadius: 14,
  background: `${GOLD}18`, border: `1px solid ${GOLD}55`,
  display: "flex", alignItems: "center", justifyContent: "center",
  marginBottom: 18,
};

const iconCircleSmall = {
  width: 32, height: 32, borderRadius: 9,
  background: `${GOLD}18`, border: `1px solid ${GOLD}55`,
  display: "flex", alignItems: "center", justifyContent: "center",
};

const callCoversStyle = {
  marginBottom: 18, padding: "14px 16px",
  borderRadius: 10, background: "rgba(255,255,255,0.025)",
  border: `1px solid ${GOLD}1A`,
};

function PrimaryButton({ children, onClick }) {
  return (
    <button onClick={onClick} style={{
      width: "100%", padding: "14px 0", borderRadius: 11,
      background: GOLD, color: "#0A0A0A",
      fontSize: 14, fontWeight: 800, letterSpacing: 0.3,
      border: "none", cursor: "pointer",
      boxShadow: `0 8px 24px ${GOLD}66, inset 0 1px 0 rgba(255,255,255,0.25)`,
      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
      fontFamily: "'DM Sans', sans-serif",
      transition: "transform .15s ease",
    }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}>
      {children}
    </button>
  );
}

const shellStyle = {
  minHeight: "100vh",
  background: "#050505",
  color: "#F5F0E8",
  display: "flex",
  flexDirection: "column",
  fontFamily: "'DM Sans', sans-serif",
  position: "relative",
};

export default BookCallPage;
